import React, { useState, useEffect } from 'react';
import CodeBlock from './CodeBlock';
import './CodeSuggestionsTable.css';

const CodeSuggestionsTable = ({ suggestions, context }) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' });
  const [sortedSuggestions, setSortedSuggestions] = useState(suggestions);
  const [showWeights, setShowWeights] = useState(false);
  
  // Initialize weights for all metrics
  const initializeWeights = () => {
    const weights = {};
    suggestions.forEach(s => {
      s.metrics.forEach(m => {
        if (!weights[m.name]) {
          weights[m.name] = 1.0; // Default weight of 1
        }
      });
    });
    return weights;
  };
  
  const [metricWeights, setMetricWeights] = useState(initializeWeights);

  // Render stars for scores out of 10
  const renderStars = (score) => {
    const stars = [];
    const starValue = score / 2; // Convert 10-point scale to 5-star scale
    
    for (let i = 1; i <= 5; i++) {
      if (i <= Math.floor(starValue)) {
        // Full star
        stars.push(<span key={i} className="star filled">★</span>);
      } else if (i === Math.ceil(starValue) && starValue % 1 !== 0) {
        // Half star (for odd scores)
        stars.push(<span key={i} className="star half">⯨</span>);
      } else {
        // Empty star
        stars.push(<span key={i} className="star empty">☆</span>);
      }
    }
    return stars;
  };

  // Initialize with all rows collapsed by default
  useEffect(() => {
    // Start with empty set (all collapsed)
    setExpandedRows(new Set());
  }, [suggestions]);

  // Update weights when suggestions change
  useEffect(() => {
    setMetricWeights(initializeWeights());
  }, [suggestions]);

  // Calculate weighted average score
  const getWeightedAverageScore = (metrics) => {
    let weightedSum = 0;
    let totalWeight = 0;
    
    metrics.forEach(metric => {
      const weight = metricWeights[metric.name] || 1;
      weightedSum += metric.score * weight;
      totalWeight += weight;
    });
    
    return totalWeight > 0 ? (weightedSum / totalWeight).toFixed(1) : '0.0';
  };

  // Update sorted suggestions when sort config or weights change
  useEffect(() => {
    if (!sortConfig.key) {
      setSortedSuggestions(suggestions);
      return;
    }

    const sorted = [...suggestions].sort((a, b) => {
      let aValue, bValue;

      if (sortConfig.key === 'avg') {
        // Sort by weighted average score
        aValue = parseFloat(getWeightedAverageScore(a.metrics));
        bValue = parseFloat(getWeightedAverageScore(b.metrics));
      } else {
        // Sort by specific metric
        const aMetric = a.metrics.find(m => m.name === sortConfig.key);
        const bMetric = b.metrics.find(m => m.name === sortConfig.key);
        aValue = aMetric ? aMetric.score : 0;
        bValue = bMetric ? bMetric.score : 0;
      }

      if (sortConfig.direction === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });

    setSortedSuggestions(sorted);
  }, [sortConfig, suggestions, metricWeights]);

  if (!suggestions || suggestions.length === 0) {
    return null;
  }

  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const handleWeightChange = (metricName, value) => {
    setMetricWeights(prev => ({
      ...prev,
      [metricName]: parseFloat(value)
    }));
  };

  const resetWeights = () => {
    const reset = {};
    Object.keys(metricWeights).forEach(key => {
      reset[key] = 1.0;
    });
    setMetricWeights(reset);
  };

  const toggleRow = (index) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  const toggleAll = () => {
    if (expandedRows.size === sortedSuggestions.length) {
      // All are expanded, so collapse all
      setExpandedRows(new Set());
    } else {
      // Some or none are expanded, so expand all
      const allIndices = new Set(sortedSuggestions.map((_, index) => index));
      setExpandedRows(allIndices);
    }
  };

  // Get all unique metric names across all suggestions
  const allMetricNames = new Set();
  suggestions.forEach(s => {
    s.metrics.forEach(m => allMetricNames.add(m.name));
  });
  const metricNames = Array.from(allMetricNames);

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <span className="sort-icon">↕</span>;
    }
    return sortConfig.direction === 'desc' 
      ? <span className="sort-icon active">↓</span>
      : <span className="sort-icon active">↑</span>;
  };

  return (
    <div className="code-suggestions-table">
      <div className="table-header">
        <div className="header-info">
          <span className="suggestions-title">
            <span className="suggestions-icon">💡</span>
            {suggestions.length} Code Suggestions
          </span>
          <span className="suggestions-context">{context}</span>
        </div>
        <div className="header-actions">
          <button 
            className="weights-btn"
            onClick={() => setShowWeights(!showWeights)}
          >
            ⚖️ Weights
          </button>
          <button 
            className="expand-all-btn"
            onClick={toggleAll}
          >
            {expandedRows.size === sortedSuggestions.length ? 'Collapse All' : 'Expand All'}
          </button>
        </div>
      </div>

      {showWeights && (
        <div className="weights-panel">
          <div className="weights-header">
            <h4>Customize Metric Weights</h4>
            <p>Adjust how much each metric contributes to the average score</p>
          </div>
          <div className="weights-grid">
            {metricNames.map(metricName => (
              <div key={metricName} className="weight-control">
                <label>{metricName}</label>
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  value={metricWeights[metricName] || 1}
                  onChange={(e) => handleWeightChange(metricName, e.target.value)}
                  className="weight-slider"
                />
                <span className="weight-value">
                  {(metricWeights[metricName] || 1).toFixed(1)}x
                </span>
              </div>
            ))}
          </div>
          <div className="weights-footer">
            <button className="reset-weights-btn" onClick={resetWeights}>
              Reset to Default
            </button>
            <span className="weights-hint">
              0 = Ignore | 1 = Normal | 2 = Double importance
            </span>
          </div>
        </div>
      )}

      <div className="table-container">
        <table className="suggestions-table">
          <thead>
            <tr>
              <th className="version-column">Version</th>
              <th className="gen-column">Gen</th>
              <th 
                className="avg-column sortable"
                onClick={() => handleSort('avg')}
              >
                Avg Score {getSortIcon('avg')}
              </th>
              {metricNames.map(metric => (
                <th 
                  key={metric} 
                  className="metric-column sortable"
                  onClick={() => handleSort(metric)}
                >
                  {metric} {getSortIcon(metric)}
                  {metricWeights[metric] !== 1 && (
                    <span className="weight-indicator">
                      {metricWeights[metric].toFixed(1)}x
                    </span>
                  )}
                </th>
              ))}
              <th className="action-column">Code</th>
            </tr>
          </thead>
          <tbody>
            {sortedSuggestions.map((suggestion, index) => {
              const isExpanded = expandedRows.has(index);
              const avgScore = getWeightedAverageScore(suggestion.metrics);
              
              return (
                <React.Fragment key={index}>
                  <tr className={`suggestion-row ${isExpanded ? 'expanded' : ''}`}>
                    <td className="version-cell">
                      <div className="version-info">
                        <span className="version-name">
                          {suggestion.filename || `Option ${index + 1}`}
                        </span>
                        {suggestion.explanation && (
                          <div className="version-tooltip">
                            <p>{suggestion.explanation}</p>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="gen-cell">
                      {suggestion.generation && (
                        <div className={`generation-badge gen-${suggestion.generation}`}>
                          Gen {suggestion.generation}
                        </div>
                      )}
                    </td>
                    <td className="avg-cell">
                      <div className="avg-score">
                        <span className="score-value">{avgScore}</span>
                        <div className="score-bar">
                          <div 
                            className="score-fill" 
                            style={{ width: `${(avgScore / 10) * 100}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    {metricNames.map(metricName => {
                      const metric = suggestion.metrics.find(m => m.name === metricName);
                      if (!metric) {
                        return <td key={metricName} className="metric-cell">-</td>;
                      }
                      
                      return (
                        <td key={metricName} className="metric-cell">
                          <div className="metric-display">
                            <div className="metric-stars">
                              {renderStars(metric.score)}
                            </div>
                            <span className="metric-score">{metric.score}/10</span>
                            <div className="metric-tooltip">
                              <strong>{metric.name}</strong>
                              <p>{metric.description}</p>
                            </div>
                          </div>
                        </td>
                      );
                    })}
                    <td className="action-cell">
                      <button
                        className="toggle-code-btn"
                        onClick={() => toggleRow(index)}
                      >
                        {isExpanded ? 'Hide' : 'View'} Code
                      </button>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="code-row">
                      <td colSpan={metricNames.length + 4}>
                        <div className="code-container">
                          <CodeBlock
                            code={suggestion.code}
                            language={suggestion.language || 'javascript'}
                            filename={suggestion.filename}
                            isDiff={suggestion.isDiff || false}
                            defaultExpanded={true}
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="table-footer">
        <div className="legend">
          <span className="legend-title">Score Legend:</span>
          <div className="legend-items">
            <span className="legend-item">★★★★★ Excellent (9-10)</span>
            <span className="legend-item">★★★★☆ Good (7-8)</span>
            <span className="legend-item">★★★☆☆ Average (5-6)</span>
            <span className="legend-item">★★☆☆☆ Below Average (3-4)</span>
            <span className="legend-item">★☆☆☆☆ Poor (1-2)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CodeSuggestionsTable; 